import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import {
  INSTALL_COACH_DISMISS_KEY,
  shouldShowInstallCoach,
} from "./install-coach";
import { pushPromptOverlay } from "./prompt-overlay";

function isStandalone(): boolean {
  const mql = window.matchMedia?.("(display-mode: standalone)").matches ?? false;
  const iosStandalone =
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return mql || iosStandalone;
}

function readDismissed(): boolean {
  try {
    return localStorage.getItem(INSTALL_COACH_DISMISS_KEY) === "1";
  } catch {
    return false; // private mode → treat as not dismissed, never throw
  }
}

export function useInstallCoach(): { visible: boolean; dismiss: () => void; dismissed: boolean } {
  const location = useLocation();
  const [dismissed, setDismissed] = useState<boolean>(readDismissed);

  const dismiss = useCallback(() => {
    setDismissed(true);
    try {
      localStorage.setItem(INSTALL_COACH_DISMISS_KEY, "1");
    } catch {
      // swallow write failures (private mode)
    }
  }, []);

  const visible = shouldShowInstallCoach({
    userAgent: navigator.userAgent,
    standalone: isStandalone(),
    pathname: location.pathname,
    dismissed,
  });

  // The install prompt has top priority — register its scrim while it's up so
  // the feature announcement cards defer until it's dismissed.
  useEffect(() => {
    if (!visible) return;
    return pushPromptOverlay();
  }, [visible]);

  return { visible, dismiss, dismissed };
}
